import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@material-ui/core';

import {
    GridRow,
    GridCol,
    Typography,
    Variant,
    Police,
    Button,
    ButtonVariant,
    IconTooltip,
    ColorDSD,
    ColorHex,
    ContainerMui,
    BorderRadiusMui
} from '../../styles';
import {
    IResumeOffre,
    TypeAssurance,
    hasAlreadyAssurance,
    hasSoumissionAssurance,
    isRevisableFerme,
    isVariableProtege,
    isVariableReduit
} from '../../hooks';
import { PretActuelModal, DetailsOffreModalChoixCoEmprunteur } from '..';
import { NoteTauxInteret } from './noteTauxInteret/NoteTauxInteret';

export interface IResumePretProps {
    resumeOffre: IResumeOffre;
    isEditButtonDisplayed?: boolean;
    isTauxAssuranceDisplayed: boolean;
    isSoldeSansAssuranceDisplayed: boolean;
    showButtonPretActualModal?: boolean;
    onModifiedClick: () => void;
    onTooltipActive: () => void;
}

export const ResumePretSimplifie: React.FC<IResumePretProps> = ({
    resumeOffre,
    isEditButtonDisplayed,
    isTauxAssuranceDisplayed,
    showButtonPretActualModal,
    isSoldeSansAssuranceDisplayed,
    onModifiedClick,
    onTooltipActive
}) => {
    const [t] = useTranslation(['ResumePret']);

    const { titreOffre, assurance, offreChoisie } = resumeOffre;
    const { codeAssurance, typeAssurance } = assurance;
    const {
        valeurTauxPropose,
        valeurTauxEffectif,
        tauxAdditionnelAssurance,
        montantRemboursementSansAssurance
    } = offreChoisie;

    const hasAssurance = hasAlreadyAssurance(codeAssurance);
    const isSoumissionAssurance = hasSoumissionAssurance(codeAssurance);
    const isOfferVariableProtege = isVariableProtege(resumeOffre?.offreChoisie);
    const isOfferVariableReduit = isVariableReduit(resumeOffre?.offreChoisie);
    const isOffreRevisableFerme = isRevisableFerme(resumeOffre?.offreChoisie);
    const shoudTooltipBeDisplayed =
        isOfferVariableReduit ||
        isOfferVariableProtege ||
        isOffreRevisableFerme;

    const renderInterestRate = () => {
        const interestRateValue =
            isOfferVariableProtege && valeurTauxEffectif
                ? valeurTauxEffectif
                : valeurTauxPropose;

        return t('Number:percent', {
            value: interestRateValue
        });
    };

    const getLibelleInfobulle = () => {
        if (isOfferVariableReduit) {
            return t('resumePretInterestLibelleInfobulleOffreVariableReduit');
        } else if (isOfferVariableProtege) {
            return (
                t('resumePretInterestLibelleInfobulleOffreVariableProtege1') +
                t('Number:percent', {
                    value: offreChoisie.valeurTauxPlafond
                }) +
                t('resumePretInterestLibelleInfobulleOffreVariableProtege2')
            );
        } else if (isOffreRevisableFerme) {
            return (
                t('resumePretInterestLibelleInfobulleOffreRevisable1') +
                t('Number:percent', {
                    value: offreChoisie.tauxRabaisSubsequent
                }) +
                t('resumePretInterestLibelleInfobulleOffreRevisable2')
            );
        } else return 'erreur libelle infobulle non trouvé';
    };

    const renderInterestTooltip = () => {
        const interetPretTooltipKey = getLibelleInfobulle();

        return (
            <Box paddingTop="7px" paddingLeft="0.5rem">
                <IconTooltip
                    iconName="alertes_contour_information"
                    srOnlySuffix={t(
                        'resumePretInterestLibelleInfobulleHorsEcranOffreVariableReduitProtegeRevisable'
                    )}
                    label={t(interetPretTooltipKey)}
                    onActive={onTooltipActive}
                />
            </Box>
        );
    };

    return (
        <>
            <GridRow>
                <GridCol col="12">
                    <Typography variant={Variant.p} police={Police.bold}>
                        {t('resumePretTitle')}
                    </Typography>
                    <ContainerMui
                        backgroundColor={ColorHex.lightGrey2}
                        borderRadius={BorderRadiusMui.sm}
                        dataTestId="nouveau_pret"
                        spacerDistance={6}
                        spacerDistanceMobile={5}
                    >
                        <Box ml="1rem" pt="0.5rem">
                            <GridRow>
                                <Typography
                                    variant={Variant.h5}
                                    hideMarginBottom
                                >
                                    {titreOffre}
                                </Typography>
                            </GridRow>
                            <Box mt={'0.75rem'}>
                                <GridRow>
                                    <Typography variant={Variant.h5}>
                                        {t('resumePretInterestTitle')}
                                    </Typography>
                                    {shoudTooltipBeDisplayed &&
                                        renderInterestTooltip()}
                                    <Box ml="0.75rem" mt="-0.1rem" mr="0.5rem">
                                        <Typography
                                            variant={Variant.h4}
                                            color={ColorDSD.primary}
                                        >
                                            {renderInterestRate()}
                                        </Typography>
                                    </Box>
                                    <NoteTauxInteret
                                        isSimplifie={true}
                                        resumeOffre={resumeOffre}
                                    />
                                </GridRow>
                            </Box>
                        </Box>
                    </ContainerMui>
                </GridCol>
                {hasAssurance && isTauxAssuranceDisplayed && (
                    <GridCol col="12" sm="12" md="4">
                        <Typography variant={Variant.p} marginBottom="0.5rem">
                            {t(
                                typeAssurance === TypeAssurance.AP
                                    ? 'resumePretInsuranceTitleAssurance'
                                    : 'resumePretInsuranceTitleAssuranceMargeAtout'
                            )}
                        </Typography>
                        <Box mb={3}>
                            <strong>
                                {t('Number:percent3Decimal', {
                                    value: tauxAdditionnelAssurance
                                })}
                            </strong>
                        </Box>
                    </GridCol>
                )}
                {isSoumissionAssurance && isSoldeSansAssuranceDisplayed && (
                    <GridCol col="12" sm="12" md="4">
                        <Typography variant={Variant.p} marginBottom="0.5rem">
                            {t('resumePretInsuranceTitlePaiementSansAssurance')}
                        </Typography>
                        <Box mb={3}>
                            <strong>
                                {t('Number:currency', {
                                    value: montantRemboursementSansAssurance
                                })}
                            </strong>
                        </Box>
                    </GridCol>
                )}
                {isEditButtonDisplayed && (
                    <GridCol col="12" sm="12" md="12">
                        <Box mb={2}>
                            <Button
                                id={'QA_test_validation_date_modifier'}
                                variant={ButtonVariant.compact}
                                dataCy="qa-button-date-modifier"
                                dataTestId={'bouton_modifier_offre'}
                                onClick={onModifiedClick}
                                srOnlySuffix={
                                    t('resumeChangeButton') +
                                    ' ' +
                                    t('resumePretTypeTitle')
                                }
                            >
                                {t('resumeChangeButton')}
                            </Button>
                        </Box>
                    </GridCol>
                )}
            </GridRow>
            <Box>
                {showButtonPretActualModal && (
                    <GridRow>
                        <GridCol col="12" sm="12" md="4">
                            <DetailsOffreModalChoixCoEmprunteur
                                resumeOffre={resumeOffre}
                            />
                        </GridCol>
                        <GridCol col="12" sm="12" md="4">
                            <PretActuelModal />
                        </GridCol>
                    </GridRow>
                )}
            </Box>
        </>
    );
};
